import { View, Pressable } from 'react-native';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import React, { useRef, useState } from 'react';
import TextLato from '../text/text-lato/TextLato';
import { styleMobile, styleDesktop } from './HelpButton.style';
import { Icon } from '../icon/Icon';
import { brand } from 'src/theme/colors';
import { TestIDs } from 'src/constants/testIDs.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { BasePressTooltip } from '../tooltips/base-press-tooltip/BasePressTooltip';
import { TooltipPosition } from '../tooltips/Tooltip.types';
import { BUTTON_SIZES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { IconQuaternaryOnLightButton } from 'src/editor-v3/components/icon-buttons/icon-quaternary-on-light-button/IconQuaternaryOnLightButton';
import { HelpButtonProps } from './HelpButton.types';

export const HelpButton = observer(({ focusOrder, focusId, focusGroup }: HelpButtonProps) => {
  const {
    dynamicModal,
    i18n,
    tutorial,
    bottomSheet,
    project: { activeGroup, activeProject },
  } = useStore();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const ref = useRef(null);
  useOnClickOutside(ref, e => {
    e.stopPropagation();
    setShowPopup(false);
  });

  const { isDesktop } = useResponsiveScreen();
  const style = isDesktop ? styleDesktop : styleMobile;

  const Content = ({ onClose }: { onClose?: () => void }) => (
    <>
      <Pressable
        style={style.option}
        onPress={() => {
          setShowPopup(false);
          onClose && onClose();
          isDesktop && dynamicModal.openModal({ dynamicModalView: DYNAMIC_MODAL_VIEWS.PrivacyPolicy });
          !isDesktop && bottomSheet.openBottomSheet({ bottomSheetView: BOTTOM_SHEET_VIEWS.PrivacyPolicy });
        }}
      >
        <View style={style.icon}>
          <Icon name={ICON_NAMES.Subscription} color={brand.main.water} />
        </View>
        <TextLato style={style.text}>{i18n.t('help-button:privacy')}</TextLato>
      </Pressable>
      {!!tutorial.firstProductRef && !activeProject?.isEmpty && (
        <Pressable
          style={style.option}
          onPress={() => {
            setShowPopup(false);
            onClose && onClose();
            tutorial.showTutorial({
              tutorialId: activeGroup ? 'ProductToolbar' : 'Group',
              stepId: 'step-1',
              isDesktop: isDesktop,
            });
          }}
        >
          <View style={style.icon}>
            <Icon name={ICON_NAMES.Eye} color={brand.main.water} />
          </View>
          <TextLato style={style.text}>{i18n.t('help-button:tutorial')}</TextLato>
        </Pressable>
      )}
    </>
  );

  return (
    <>
      <View style={style.wrapper}>
        {showPopup && (
          <View style={style.popup} ref={ref}>
            <Content />
          </View>
        )}
        {isDesktop && (
          <CircularButton
            size={CIRCULAR_BUTTON_SIZES.LARGE}
            type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
            iconName={ICON_NAMES.Question1}
            iconStyle={style.buttonIcon}
            onPress={async () => setShowPopup(!showPopup)}
            style={style.button}
            accessible={true}
            accessibilityLabel={i18n.t('help-button:open')}
            accessibilityRole="button"
            focusId={focusId}
            focusOrder={focusOrder}
            focusGroup={focusGroup}
          />
        )}
      </View>
      {!isDesktop && (
        <BasePressTooltip
          TooltipContent={Content}
          position={TooltipPosition.BOTTOM}
          tooltipID={TestIDs.TOOLTIP_DISCOUNT}
          showTooltipArrow
          TooltipButton={({ onPress }) => (
            <IconQuaternaryOnLightButton
              onPress={async () => onPress()}
              size={BUTTON_SIZES.SMALL}
              iconName={ICON_NAMES.Help}
              accessible={true}
              accessibilityLabel={i18n.t('help-button:open')}
              accessibilityRole="button"
              focusId={focusId}
              focusOrder={focusOrder}
              focusGroup={focusGroup}
            />
          )}
        />
      )}
    </>
  );
});
